import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormHelperText,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { modal } from "../../../../util";
import { toast } from "react-toastify";
import { useLoading } from "../../../../util/LoadingProvider";
import { useHistory } from "react-router";
import UploadImage from "../../UploadImage";
import apiHelper from "../../../../util/ApiHelper";
import {
  // KeyboardDatePicker,
  KeyboardDateTimePicker,
  // KeyboardTimePicker,
} from "@material-ui/pickers";

const schema = yup.object().shape({
  name: yup.string(),
  url: yup
    .string()
    .nullable()
    .optional()
    .matches(RegExp(/http:\/\/|https:\/\//g), {
      message: "Missing http:// or https://",
      excludeEmptyString: true,
    }),
  image: yup.string(),
  imageMobile: yup.string(),
});

const defaultFormValues = {
  name: "",
  redirectUrl: "",
  image: "",
  imageMobile: "",
  startTime: null,
  endTime: null,
  status: 0,
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  sectionText: {
    fontSize: "13px",
    fontWeight: 700,
    paddingLeft: "4px",
  },
  imageGallery: {
    border: "1px solid #CCCCCC",
    minHeight: "160px",
    borderRadius: "3px",
    position: "relative",
  },
  imageGalleryControl: {
    position: "relative",
    width: "100%",
    display: "flex",
    height: "40px",
    bottom: "0",
    borderTop: "1px solid #CCCCCC",
    padding: "5px",
  },
  imageGalleryBlk: {
    minHeight: "120px",
    padding: "5px",
  },
  imgPreview: {
    maxWidth: "200px",
    margin: "10px",
    display: "inline-flex",
    "& img": {
      width: "100%",
      height: "auto",
    },
  },
}));

export default function BannerEditPage({ disabled, ...props }) {
  const { match } = props;
  const history = useHistory();
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    // console.log("value:", value);
    if (id !== "new") updateBanner(value);
    else createBanner(value);
  };

  const getBanner = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getHkiffAcaBannerById(id);
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, reset, setLoading]);

  const updateBanner = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateHkiffAcaBannerById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
        history.push(`/hkiff/aca/banner`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createBanner = async (value) => {
    try {
      const result = await apiHelper.hkiff.createHkiffAcaBanner(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("The banner is created.");
        history.push(`/hkiff/aca/banner/${result.data.id}`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deleteBanner = async () => {
    const decision = await modal.fire({
      title: "Delete Banner",
      text: "Are you sure to delete this banner?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteAcaBannerById(id);
        console.log(result);
        if (result.data.code === 0) {
          history.push(`/hkiff/aca/banner`);
          toast.dark("Banner deleted.");
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    console.log("id", id);
    if (id !== "new") getBanner();
  }, [getBanner, id]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Banner &gt; {id === "new" ? "Add Banner" : "Edit Banner"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="row" spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Banner Name"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="redirectUrl"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Redirect Url"
                      variant="outlined"
                      margin="dense"
                      className={classes.textInput}
                      fullWidth
                      {...field}
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                {defaultValues && (
                  <UploadImage
                    name="image"
                    data={defaultValues}
                    control={control}
                    Controller={Controller}
                    setValue={setValue}
                    getValues={getValues}
                    disabled={disabled}
                    uploadLabel="Upload Desktop Image"
                    changeLabel="Change Desktop Image"
                    uploadFolder="ACA_BANNER"
                  />
                )}
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                {defaultValues && (
                  <UploadImage
                    name="imageMobile"
                    data={defaultValues}
                    control={control}
                    Controller={Controller}
                    setValue={setValue}
                    getValues={getValues}
                    disabled={disabled}
                    uploadLabel="Upload Mobile Image"
                    changeLabel="Change Mobile Image"
                    uploadFolder="ACA_BANNER"
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="column" spacing={3}>
              <Grid item xs={12}>
                <strong className={classes.sectionText}>
                  Display Settings
                </strong>
              </Grid>
              <Grid container item xs={12}>
                <Grid container item xs={6} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name={`startTime`}
                      control={control}
                      render={({
                        field: { value, ...rest },
                        fieldState: { error },
                      }) => {
                        return (
                          <KeyboardDateTimePicker
                            id={`startTime-picker`}
                            fullWidth
                            variant="dialog"
                            format="d MMM yyyy, hh:mma"
                            margin="dense"
                            label="Start Date"
                            {...rest}
                            disabled={disabled}
                            value={value}
                            onChange={(value) => {
                              setValue(
                                "startTime",
                                (dayjs(value).isValid())?
                                    dayjs(value).format("YYYY-MM-DDTHH:mm:00") : null
                              );
                            }}
                            InputLabelProps={{
                              shrink: true,
                              style: {
                                fontWeight: 700,
                                color: "#333333",
                              },
                            }}
                            KeyboardButtonProps={{
                              "aria-label": "change date and time",
                            }}
                            minutesStep={5}
                            ampm={false}
                            onError={console.log}
                            disablePast={false}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name={`endTime`}
                      control={control}
                      render={({
                        field: { value, onChange, ...rest },
                        fieldState: { error },
                      }) => {
                        return (
                          <KeyboardDateTimePicker
                            id={`endTime-picker`}
                            fullWidth
                            minutesStep={5}
                            disabled={disabled}
                            margin="dense"
                            variant="dialog"
                            ampm={false}
                            label="End Date"
                            InputLabelProps={{
                              shrink: true,
                              style: {
                                fontWeight: 700,
                                color: "#333333",
                              },
                            }}
                            value={value}
                            onChange={(value) => {
                              setValue(
                                "endTime",
                                (dayjs(value).isValid())?
                                    dayjs(value).format("YYYY-MM-DDTHH:mm:00") : null
                              );
                            }}
                            onError={console.log}
                            disablePast={false}
                            format="d MMM yyyy, hh:mma"
                            error={!!error}
                            helperText={error ? error.message : ""}
                            {...rest}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  spacing={3}
                  style={{ marginTop: "52px", marginLeft: 0 }}
                >
                  <Grid item xs={3} style={{ paddingLeft: "4px" }}>
                    <Controller
                      control={control}
                      defaultValue={defaultFormValues?.status}
                      name="status"
                      render={({
                        field: { value, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <ToggleButtonGroup
                          {...rest}
                          value={value ? 1 : 0}
                          onChange={(event, newFormats) => {
                            onChange(newFormats);
                          }}
                          exclusive
                          aria-label="Status"
                          className={classes.input}
                        >
                          <ToggleButton
                            value={1}
                            aria-label="Active"
                            style={{ width: "50%" }}
                            disabled={disabled}
                          >
                            Active
                          </ToggleButton>
                          <ToggleButton
                            value={0}
                            aria-label="Inactive"
                            style={{ width: "50%" }}
                            disabled={disabled}
                          >
                            Inactive
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={3}></Grid>
              </Grid>
            </Grid>
          </Box>

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Save Changes
              </Button>
              {match.params.id && (
                <Button
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#962013",
                    padding: "9px 16px",
                  }}
                  onClick={() => {
                    deleteBanner();
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
